import { getIn } from 'icepick';
import { isTravelQuestion } from 'event-widgets/utils/travelUtils';
import { getRegistrationQuestions, getQuestionVisibilityFilters,
  getProductQuestions } from 'event-widgets/redux/selectors/appData';
import TextAndDateAnswer from '../widgets/QuestionsAndFields/model/TextAndDateAnswer';
import moment from 'moment';

export const TRAVEL_ANSWER_REQUESTED_ACTIONS = {
  ADD: 'ADD',
  MODIFY: 'MODIFY',
  DELETE: 'DELETE',
  VIEW: 'VIEW'
};

/**
 * Returns survey type from config object
 * @param {Object} config
 * @returns {string}
 */
export function getQuestionSurveyType(config) {
  return getIn(config, ['appData', 'question', 'additionalInfo', 'surveyType']);
}

/**
 * Returns Field name for product and travel questions
 * @param {Object} props
 * @returns {string}
 */
export function getQuestionFieldName(props) {
  if (isTravelQuestion(getQuestionSurveyType(props.config))) {
    return props.config.id + (props.booking.id ? '-' + props.booking.id : '');
  }
  return props.config.id + (props.eventRegistrationId ? '-' + props.eventRegistrationId : '');
}

/**
 * Returns answer from userSession or travelCart
 * @param {Object} state
 * @param {Array} path
 * @param {string} eventRegistrationId
 * @returns {Object}
 */
export function getTravelAnswerData(state, path, eventRegistrationId) {
  // check for answer in userSession
  let answer = getIn(state, ['travelCart', ...path]);
  // check for answer in travelCart
  if (!answer) {
    const existingBooking = (state.travelCart.cart.bookings || [])
      .find((booking) => booking.id === eventRegistrationId);
    if (existingBooking) {
      const [questionId, requestBookingId] = path[2].split('_');
      answer = existingBooking.travelAnswers
        .find((ans) => ans.questionId === questionId && ans.requestBookingId === requestBookingId &&
          ans.requestedAction !== TRAVEL_ANSWER_REQUESTED_ACTIONS.DELETE);
      return answer;
    }
  }
  return answer;
}

/**
 * Returns path array to fetch travel answer
 * @param {string} questionId
 * @param {string} bookingId
 * @returns {Array}
 */
export function buildTravelQuestionAnswerPath(questionId, bookingId) {
  return [
    'userSession',
    'travelAnswers',
    `${questionId}_${bookingId}`
  ];
}

export const isProductQuestion = (state, questionId) => {
  return !!getIn(state, ['registrationSettings', 'productQuestions', questionId]);
};

export const isGuestQuestion = (state, questionId) => {
  const question = getIn(state, ['appData', 'registrationSettings', 'registrationQuestions', questionId]);
  let isGuest = false;

  if (question) {
    const audienceType = question.question.additionalInfo.audienceType;

    if (audienceType && audienceType === 'GuestOnly') {
      isGuest = true;
    }
  }

  return isGuest;
};

const reducer = (fieldIds, node) => {
  if (node && node.nodeType === 'Criterion') {
    if (!fieldIds.includes(node.fieldName)) {
      fieldIds.push(node.fieldName);
    }
  } else if (node && node.nodeType === 'Filter') {
    fieldIds.concat(node.filters.reduce(reducer, fieldIds));
  }
  return fieldIds;
};
const getFieldIdsFromFilters = (filters) => {
  return filters.reduce(reducer, []);
};

export const getQuestionIdsForVisibilityField = (state, fieldId, isGuest, regPathId) => {
  const questionIds = [];
  let questions = getRegistrationQuestions(state);
  const productQuestions = getProductQuestions(state.appData);
  if (productQuestions) {
    questions = {
      ...questions,
      ...productQuestions
    };
  }
  if (!questions) {
    return questionIds;
  }
  Object.keys(questions).filter(questionId => {
    if (questions[questionId].registrationPathQuestionAssociations &&
      questions[questionId].registrationPathQuestionAssociations.includes(regPathId)) {
      const filters = getQuestionVisibilityFilters(state, questionId);

      if (filters && isGuest === isGuestQuestion(state, questionId)) {
        const fieldIds = getFieldIdsFromFilters(filters);
        if (fieldIds.includes(fieldId)) {
          questionIds.push(questionId);
        }
      }
    }
  });


  return questionIds;
};

export const getQuestionVisibilityFieldIdsForQuestions = (state, questionIds) => {
  const fieldIds = [];
  questionIds.forEach(questionId => {
    const filters = getQuestionVisibilityFilters(state, questionId);
    if (filters) {
      const idsFromFilters = getFieldIdsFromFilters(filters);
      idsFromFilters.forEach(fieldId => {
        if (!fieldIds.includes(fieldId)) {
          fieldIds.push(fieldId);
        }
      });
    }
  });
  return fieldIds;
};

export const questionHasVisibilityLogic = (state, questionId) => {
  let questionType = 'registrationQuestions';
  if (isProductQuestion(state.appData, questionId)) {
    questionType = 'productQuestions';
  }
  const filters = getIn(state, ['appData', 'registrationSettings', questionType, questionId,
    'question', 'visibilityLogic', 'filters']);
  return filters && filters.length;
};

export const getQuestionIdsWithVisibilityLogic = (state, isGuest, regPathId) => {
  let questions = getRegistrationQuestions(state);
  const productQuestions = getProductQuestions(state.appData);
  if (productQuestions) {
    questions = {
      ...questions,
      ...productQuestions
    };
  }
  if (questions) {
    const questionIds = Object.keys(questions);
    return questionIds.filter(questionId => {
      const isOnRegPath = questions[questionId].registrationPathQuestionAssociations &&
        questions[questionId].registrationPathQuestionAssociations.includes(regPathId);
      const filters = getIn(questions[questionId], ['question', 'visibilityLogic', 'filters']);
      return isOnRegPath && filters && filters.length && isGuest === isGuestQuestion(state, questionId);
    });
  }
  return [];
};

export function createAnswer(fieldId, value, isDate) {
  if (Array.isArray(value)) {
    return new TextAndDateAnswer(fieldId, value.map(answer => {
      let answerValue = answer;
      if (answerValue) {
        const date = moment(answerValue);
        if (date.isValid() && isDate) {
          /*
           * Visibilty logic can only be based on the actual date, not the time. So it's fine for the times to differ.
           * Need to do this to avoid timezones.
           */
          answerValue = date.utc().hours(12).toISOString();
        } else {
          answerValue = answerValue.toLowerCase();
        }
      }
      return { answerType: 'Text', text: answerValue || '' };
    }));
  }
  let answerValue = value;
  if (answerValue) {
    const date = moment(answerValue);
    if (date.isValid() && isDate) {
      /*
       * Visibilty logic can only be based on the actual date, not the time. So it's fine for the times to differ.
       * Need to do this to avoid timezones.
       */
      answerValue = date.utc().hours(12).toISOString();
    } else {
      answerValue = answerValue.toLowerCase();
    }
  }
  return new TextAndDateAnswer(fieldId, [{ answerType: 'Text', text: answerValue || '' }]);
}
